import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Avatar,
  Dialog,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import DOMPurify from "dompurify";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";

// Unsigned client-side upload to the bot owner's own Cloudinary account.
const uploadToCloudinary = async (file, cfg) => {
  const form = new FormData();
  form.append("file", file);
  form.append("upload_preset", cfg.uploadPreset);
  if (cfg.folder) form.append("folder", cfg.folder);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${cfg.cloudName}/auto/upload`, { method: "POST", body: form });
  if (!res.ok) throw new Error(`Cloudinary upload failed (${res.status})`);
  const data = await res.json();
  if (!data.secure_url) throw new Error("Cloudinary: no secure_url");
  return data.secure_url;
};

const SMILIES = ["😡", "😟", "😐", "🙂", "😍"];
const DEFAULT_LABELS = ["Terrible", "Bad", "Okay", "Good", "Great"];

const validateAnswer = (answer, question) => {
  if (!question) return { valid: true };
  const { type, validations } = question;
  const msg = question.errorMessage || "Please enter a valid answer";

  if (type === "email_feild" || type === "email") {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answer)) return { valid: false, error: msg };
  }
  if (type === "website") {
    if (!/^https?:\/\/.+\..+/.test(answer.trim())) return { valid: false, error: msg || "Please enter a valid URL (https://...)" };
  }
  if (type === "number") {
    if (answer.trim() === "" || isNaN(Number(answer))) return { valid: false, error: msg };
    const num = Number(answer);
    if (validations?.minLength != null && num < validations.minLength) return { valid: false, error: msg };
    if (validations?.maxLength != null && num > validations.maxLength) return { valid: false, error: msg };
  }
  if (type === "mobile_number") {
    if (answer.replace(/\D/g, "") !== answer.trim()) return { valid: false, error: msg };
    if (validations?.minLength != null && answer.length < validations.minLength) return { valid: false, error: msg };
    if (validations?.maxLength != null && answer.length > validations.maxLength) return { valid: false, error: msg };
  }
  if (validations?.minLength != null && answer.length < validations.minLength) return { valid: false, error: msg };
  if (validations?.maxLength != null && answer.length > validations.maxLength) return { valid: false, error: msg };
  if (validations?.pattern) {
    try {
      if (!new RegExp(validations.pattern).test(answer)) return { valid: false, error: msg };
    } catch {}
  }
  return { valid: true };
};

const DynamicQuestionInput = ({
  currentQ,
  optionColor,
  headerColor,
  onSelect,
  onSkip,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const apiUrl = currentQ?.settings?.apiEndpoint;
    if (!apiUrl) {
      setLoading(false);
      setError("No API endpoint configured");
      return;
    }

    const headers = { "Content-Type": "application/json" };
    (currentQ?.settings?.headers || []).forEach((h) => {
      if (h.key && h.key.trim()) headers[h.key] = h.value || "";
    });

    let parsedBody;
    if (
      currentQ?.settings?.apiMethod === "POST" &&
      currentQ?.settings?.apiBody
    ) {
      try {
        parsedBody = JSON.stringify(JSON.parse(currentQ?.settings?.apiBody));
      } catch {
        setLoading(false);
        setError("Invalid JSON in body");
        return;
      }
    }

    import("../../../../../api/botApi").then(({ proxyFetch }) => {
      proxyFetch({
        url: apiUrl,
        method: currentQ?.settings?.apiMethod || "GET",
        headers,
        body: parsedBody,
      }).then((result) => {
        if (!result || !result.ok) {
          setError(result?.error || "Failed to load options");
          setLoading(false);
          return;
        }
        const data = result.data;
        let list = Array.isArray(data)
          ? data
          : data.options || data.data || data.results || [];
        const path = (currentQ?.settings?.responsePath || "").trim();
        if (path && path !== "auto") {
          path.split(".").forEach((p) => {
            if (list && typeof list === "object") list = list[p];
          });
        }
        if (!Array.isArray(list)) list = [];
        setOptions(
          list.map((o) =>
            typeof o === "object"
              ? o[currentQ?.settings?.labelField || "label"] ||
                o[currentQ?.settings?.valueField || "value"] ||
                o.name ||
                String(o)
              : String(o),
          ),
        );
        setLoading(false);
      });
    });
  }, [currentQ]);

  if (loading) {
    return (
      <Box
        sx={{
          pr: 2,
          pb: 1.5,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 1,
        }}
      >
        <CircularProgress size={16} sx={{ color: headerColor }} />
        <Typography sx={{ fontSize: 11, color: "#6b7280" }}>
          Loading options...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ pr: 2, pb: 1.5 }}>
        <Typography sx={{ fontSize: 11, color: "#dc2626" }}>{error}</Typography>
      </Box>
    );
  }

  if (options.length === 0) {
    return (
      <Box sx={{ pr: 2, pb: 1.5 }}>
        <Typography
          sx={{ fontSize: 11, color: "#6b7280", fontStyle: "italic" }}
        >
          API returned no options
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, pb: 1.5 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {options.map((opt, i) => (
          <Button
            key={i}
            onClick={() => onSelect(typeof opt === "object" ? (opt.label || opt.value || opt.name || JSON.stringify(opt)) : opt)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12,
              px: 2,
              py: 1,
              border: "none",
              color: "#fff",
              background: `linear-gradient(135deg, ${optionColor}, ${optionColor}dd)`,
              boxShadow: `0 3px 10px ${optionColor}30`,
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: `0 6px 20px ${optionColor}50`,
                background: `linear-gradient(135deg, ${optionColor}, ${optionColor})`,
                filter: "brightness(1.05)",
              },
              "&:active": { transform: "translateY(0)" },
            }}
          >
            {typeof opt === "object" ? (opt.label || opt.value || opt.name || JSON.stringify(opt)) : opt}
          </Button>
        ))}
      </Box>
      {currentQ?.skipOption && !currentQ?.required && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Button
            onClick={onSkip}
            startIcon={<span style={{ fontSize: 14 }}>⏭</span>}
            sx={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "none",
              color: headerColor,
              border: `1.5px dashed ${headerColor}`,
              borderRadius: 2,
              px: 1.5,
              py: 0.6,
              bgcolor: "rgba(0,0,0,0.02)",
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: `${headerColor}10`,
                borderColor: headerColor,
                transform: "translateY(-1px)",
                boxShadow: `0 3px 10px ${headerColor}30`,
              },
            }}
          >
            Skip this question
          </Button>
        </Box>
      )}
    </Box>
  );
};

const BotPreviewDialogPopup = ({ open, onClose, droppedItems }) => {
  const [botSettings, setBotSettings] = useState({});
  const [step, setStep] = useState(0);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [validationError, setValidationError] = useState("");
  const [shake, setShake] = useState(false);
  const [typing, setTyping] = useState(false);
  const [dropdownVal, setDropdownVal] = useState("");
  const [otherText, setOtherText] = useState("");
  const chatRef = useRef(null);
  const isAdvancingRef = useRef(false);
  const shuffledRef = useRef(null);
  const stepRef = useRef(0);
  const items = droppedItems || [];

  useEffect(() => {
    if (open) {
      setStep(0);
      setMessages([]);
      setInput("");
      setValidationError("");
      setDropdownVal("");
      setOtherText("");
      shuffledRef.current = null;
      stepRef.current = 0;
      // Load bot settings from API
      const botId = localStorage.getItem("selectedBotId");
      if (botId) {
        import("../../../../../api/botApi").then(({ getBotById }) => {
          getBotById(botId)
            .then((data) => {
              if (data?.botSettings) setBotSettings(data.botSettings);
            })
            .catch(() => {});
        });
      }
    }
  }, [open]);

  const colors = botSettings.themeColors || {};
  const headerColor = colors.header || "#6366f1";
  const optionColor = colors.option || "#007bff";
  const botName = botSettings.botName || "Chatbot";
  const description = botSettings.description || "Online";
  const companyLogo = botSettings.companyLogo || "";
  const welcomeText = botSettings.welcomeText || "";
  const avatar = botSettings.avatar || "";

  useEffect(() => {
    if (chatRef.current)
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, typing]);

  const advance = (answer, opts = {}) => {
    if (isAdvancingRef.current) return;
    isAdvancingRef.current = true;
    setTimeout(() => { isAdvancingRef.current = false; }, 900);
    const text = opts.isSkip ? "Skipped" : answer;
    setMessages((prev) => [...prev, { sender: "user", text, isSkip: !!opts.isSkip }]);
    setValidationError("");
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setStep((prev) => {
        const q = items[prev];
        if (!opts.isSkip && q?.conditions?.length > 0 && typeof answer === "string") {
          const wildcard = q.conditions.find((c) => c.jumpTo && (c.answer === "*" || c.answer === "any" || c.answer === "all"));
          const matched = q.conditions.find((c) => c.answer && c.jumpTo && !["*", "any", "all"].includes(c.answer) && answer.toLowerCase().includes(c.answer.toLowerCase()));
          const found = matched || wildcard;
          if (found) {
            const jumpIdx = items.findIndex((f) => f.id === found.jumpTo);
            if (jumpIdx >= 0) return jumpIdx;
          }
        }
        return prev + 1;
      });
    }, 800);
  };

  const currentQ = items[step];
  const done = step >= items.length;

  useEffect(() => {
    if (stepRef.current !== step) {
      shuffledRef.current = null;
      stepRef.current = step;
      setDropdownVal("");
      setOtherText("");
      setFormValues({});
      setContactVals({});
      setSelDate("");
      setSelSlot("");
      setOtpSent(false);
      setOtpDigits([]);
      setOtpExpected("");
      setOtpResendIn(0);
      setRtsQuery("");
      setRtsSelected("");
      setRtsApiResults([]);
    }
  }, [step]);

  if (currentQ?.shuffleOptions && currentQ?.options && !shuffledRef.current) {
    const arr = [...currentQ.options];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    shuffledRef.current = arr;
  }
  if (!currentQ?.shuffleOptions) shuffledRef.current = null;
  const displayOptions = shuffledRef.current || currentQ?.options || [];
  const isDropdown = currentQ?.style === "dropdown";
  const isList = currentQ?.style === "list";
  const layoutDir = currentQ?.flexDirection || "row";
  const hasOtherOption = !!currentQ?.otherOption;

  const [ratingHover, setRatingHover] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [contactVals, setContactVals] = useState({});
  const [selDate, setSelDate] = useState("");
  const [selSlot, setSelSlot] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpDigits, setOtpDigits] = useState([]);
  const [otpExpected, setOtpExpected] = useState("");
  const [otpResendIn, setOtpResendIn] = useState(0);
  const [rtsQuery, setRtsQuery] = useState("");
  const [rtsSelected, setRtsSelected] = useState("");
  const [rtsApiResults, setRtsApiResults] = useState([]);

  useEffect(() => {
    if (otpResendIn <= 0) return;
    const t = setTimeout(() => setOtpResendIn((p) => Math.max(0, p - 1)), 1000);
    return () => clearTimeout(t);
  }, [otpResendIn]);

  useEffect(() => {
    if (currentQ?.type !== "Real_time_search") return;
    const s = currentQ?.settings || {};
    const source = (s.source || "static").toLowerCase();
    if (source !== "api" || !s.apiEndpoint || rtsQuery.length < 2) { setRtsApiResults([]); return; }
    const t = setTimeout(async () => {
      const { proxyFetch } = await import("../../../../../api/botApi");
      const res = await proxyFetch({ url: s.apiEndpoint, method: s.apiMethod || "GET", headers: { "Content-Type": "application/json" } });
      if (res?.ok) {
        let list = Array.isArray(res.data) ? res.data : res.data?.options || res.data?.data || res.data?.results || [];
        const path = (s.responsePath || "").trim();
        if (path && path !== "auto") path.split(".").forEach((p) => { if (list && typeof list === "object") list = list[p]; });
        if (!Array.isArray(list)) list = [];
        setRtsApiResults(list.map((o) => typeof o === "object" ? o[s.labelField || "label"] || o[s.valueField || "value"] || String(o) : String(o)));
      }
    }, 350);
    return () => clearTimeout(t);
  }, [rtsQuery, currentQ]);

  const renderInput = () => {
    if (done || typing) return null;
    if (!currentQ) return null;
    const type = currentQ.type;

    // Rating
    if (type === "rating") {
      const rType = currentQ.settings?.ratingType || "stars";
      const labels = currentQ.settings?.ratingLabels || DEFAULT_LABELS;
      const activeIdx = ratingHover !== null ? ratingHover : 2;
      return (
        <Box
          sx={{
            p: "30px",
            bgcolor: "#f8fafc",
            borderRadius: "10px",
            border: "1px solid #e2e8f0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                transition: "all 0.3s ease",
                transform: ratingHover !== null ? "scale(1.1)" : "scale(1)",
              }}
            >
              <Typography sx={{ fontSize: 60, lineHeight: 1 }}>
                {rType === "smilies"
                  ? SMILIES[activeIdx]
                  : rType === "numbers"
                    ? `${activeIdx + 1}`
                    : "⭐"}
              </Typography>
            </Box>
            <Typography
              sx={{ fontSize: 11, fontWeight: 600, color: "#374151" }}
            >
              {labels[activeIdx]}
            </Typography>
            <Box sx={{ display: "flex", gap: 0 }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Box
                  key={i}
                  onMouseEnter={() => setRatingHover(i)}
                  onMouseLeave={() => setRatingHover(null)}
                  onClick={() => {
                    setRatingHover(null);
                    advance(`${labels[i]} (${i + 1}/5)`);
                  }}
                  sx={{
                    cursor: "pointer",
                    p: 0.5,
                    transition: "all 0.2s",
                    transform: ratingHover === i ? "scale(1.3)" : "scale(1)",
                  }}
                >
                  <Typography sx={{ fontSize: 20 }}>
                    {rType === "smilies"
                      ? SMILIES[i]
                      : rType === "numbers"
                        ? `${i + 1}`
                        : "⭐"}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      );
    }

    // Date
    if (type === "date_picker") {
      return (
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={input}
            onChange={(e) => setInput(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 12 },
            }}
          />
          <Button
            variant="contained"
            size="small"
            disabled={!input}
            onClick={() => {
              advance(input);
              setInput("");
            }}
            sx={{ borderRadius: 2, minWidth: 36, bgcolor: headerColor }}
          >
            <SendIcon fontSize="small" />
          </Button>
        </Box>
      );
    }

    // Time
    if (type === "time_picker") {
      return (
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            type="time"
            size="small"
            fullWidth
            value={input}
            onChange={(e) => setInput(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 12 },
            }}
          />
          <Button
            variant="contained"
            size="small"
            disabled={!input}
            onClick={() => {
              advance(input);
              setInput("");
            }}
            sx={{ borderRadius: 2, minWidth: 36, bgcolor: headerColor }}
          >
            <SendIcon fontSize="small" />
          </Button>
        </Box>
      );
    }

    // Range
    if (type === "range") {
      const min = currentQ.settings?.range?.min || 0;
      const max = currentQ.settings?.range?.max || 100;
      return (
        <Box sx={{ px: 1 }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
          >
            <Typography sx={{ fontSize: 10, color: "#6b7280" }}>
              {min}
            </Typography>
            <Typography
              sx={{ fontSize: 12, fontWeight: 700, color: headerColor }}
            >
              {input || min}
            </Typography>
            <Typography sx={{ fontSize: 10, color: "#6b7280" }}>
              {max}
            </Typography>
          </Box>
          <input
            type="range"
            min={min}
            max={max}
            value={input || min}
            onChange={(e) => setInput(e.target.value)}
            style={{ width: "100%", accentColor: headerColor }}
          />
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={() => {
              advance(input || String(min));
              setInput("");
            }}
            sx={{
              mt: 0.5,
              borderRadius: 2,
              textTransform: "none",
              fontSize: 11,
              bgcolor: headerColor,
            }}
          >
            Submit
          </Button>
        </Box>
      );
    }

    // Multiple Choice
    if (type === "multiple_options") {
      const opts = currentQ.options || [];
      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {opts.map((opt, i) => {
            const label =
              typeof opt === "object" ? opt.label || opt.value : opt;
            const selected = (input || "").split(", ").includes(label);
            return (
              <Box
                key={i}
                onClick={() => {
                  const current = input
                    ? input.split(", ").filter(Boolean)
                    : [];
                  const next = selected
                    ? current.filter((x) => x !== label)
                    : [...current, label];
                  const joined = next.join(", ");
                  setInput(joined);
                  if (joined) advance(joined);
                }}
                sx={{
                  borderRadius: 2.5,
                  background: selected
                    ? `linear-gradient(135deg, ${optionColor}, ${optionColor}dd)`
                    : "#fff",
                  color: selected ? "#fff" : "#374151",
                  border: selected ? "none" : `1.5px solid #e5e7eb`,
                  px: 2.5, py: 1.1,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  boxShadow: selected ? `0 3px 10px ${optionColor}30` : "none",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: `0 4px 12px ${optionColor}30`,
                  },
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>
      );
    }

    // Dynamic Question (API-driven options)
    if (type === "dynamic_question") {
      return (
        <DynamicQuestionInput
          currentQ={currentQ}
          optionColor={optionColor}
          headerColor={headerColor}
          onSelect={advance}
          onSkip={() => advance("", { isSkip: true })}
        />
      );
    }

    // File Upload
    if (type === "file_upload") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pr: 2, pb: 1.5 }}>
          <Box
            onClick={() => document.getElementById("talktokam-preview-file-input")?.click()}
            sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              px: 2, py: 1.2, cursor: "pointer",
              borderRadius: 2, border: `1.5px dashed ${headerColor}`,
              bgcolor: "rgba(0,0,0,0.02)", color: headerColor,
              fontSize: 12, fontWeight: 700, textTransform: "none",
              transition: "all 0.2s ease",
              "&:hover": { bgcolor: `${headerColor}10`, transform: "translateY(-1px)", boxShadow: `0 4px 12px ${headerColor}30` },
            }}
          >
            <span style={{ fontSize: 16 }}>📎</span>
            Choose a file
          </Box>
          <Typography sx={{ fontSize: 10, color: "#9ca3af" }}>JPG, PNG, PDF, DOC · max {currentQ?.settings?.uploadLimit ?? 10}MB</Typography>
          <input id="talktokam-preview-file-input" type="file" hidden
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const maxMb = currentQ?.settings?.uploadLimit ?? 10;
              if (file.size > maxMb * 1024 * 1024) { e.target.value = ""; return; }
              const isImage = file.type.startsWith("image/");
              const cloud = currentQ?.settings?.cloudinary;
              if (cloud?.enabled && cloud?.cloudName && cloud?.uploadPreset) {
                try {
                  const url = await uploadToCloudinary(file, cloud);
                  advance(isImage ? `[IMG]${url}` : `📎 ${file.name} ${url}`);
                } catch (err) {
                  advance(`📎 ${file.name}`);
                }
                e.target.value = "";
                return;
              }
              if (isImage) {
                const reader = new FileReader();
                reader.onload = () => {
                  advance(`[IMG]${reader.result}`);
                  e.target.value = "";
                };
                reader.readAsDataURL(file);
              } else {
                advance(`📎 ${file.name}`);
                e.target.value = "";
              }
            }}
          />
        </Box>
      );
    }

    // Single Choice / Options
    if (
      currentQ.options &&
      Array.isArray(currentQ.options) &&
      currentQ.options.length > 0 &&
      type !== "multiple_options"
    ) {
      const opts = displayOptions;
      const getLabel = (opt) => (typeof opt === "object" ? opt.label || opt.value : opt);
      return (
        <Box>
          {isDropdown ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 220 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Choose an option</InputLabel>
                <Select
                  value={dropdownVal}
                  label="Choose an option"
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "__other__") {
                      setDropdownVal("__other__");
                      setOtherText("");
                    } else if (val) {
                      advance(val);
                      setDropdownVal("");
                    }
                  }}
                  sx={{ borderRadius: 2, bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                >
                  {opts.map((opt, i) => (
                    <MenuItem key={i} value={getLabel(opt)}>
                      {currentQ.imageChoices?.[i]?.image && (
                        <img src={currentQ.imageChoices[i].image} alt="" style={{ width: 20, height: 20, borderRadius: 4, objectFit: "cover", marginRight: 8 }} />
                      )}
                      {getLabel(opt)}
                    </MenuItem>
                  ))}
                  {hasOtherOption && <MenuItem value="__other__">Other</MenuItem>}
                </Select>
              </FormControl>
              {dropdownVal === "__other__" && (
                <TextField
                  fullWidth size="small" placeholder="Type your answer and press Enter..."
                  value={otherText}
                  autoFocus
                  onChange={(e) => setOtherText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && otherText.trim()) {
                      advance(otherText.trim());
                      setOtherText("");
                      setDropdownVal("");
                    }
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}
                />
              )}
            </Box>
          ) : isList ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
              {opts.map((opt, i) => (
                <Button
                  key={i}
                  fullWidth size="small"
                  onClick={() => advance(getLabel(opt))}
                  sx={{
                    borderRadius: `${Math.max(8, 8)}px`,
                    textTransform: "none",
                    fontSize: 13, fontWeight: 600,
                    background: `linear-gradient(135deg, ${optionColor}, ${optionColor}dd)`,
                    color: "#fff", border: "none",
                    px: 2.2, py: 0.9,
                    justifyContent: "flex-start", display: "flex", alignItems: "center", gap: 1,
                    boxShadow: `0 3px 10px ${optionColor}30`,
                    "&:hover": { filter: "brightness(1.1)", transform: "translateY(-1px)", boxShadow: `0 6px 16px ${optionColor}50` },
                    "&:active": { transform: "translateY(0)" },
                  }}
                >
                  {currentQ.imageChoices?.[i]?.image && (
                    <img src={currentQ.imageChoices[i].image} alt="" style={{ width: 20, height: 20, borderRadius: 4, objectFit: "cover" }} />
                  )}
                  {getLabel(opt)}
                </Button>
              ))}
              {hasOtherOption && (
                <Button
                  fullWidth size="small"
                  onClick={() => { setOtherText(""); setDropdownVal("__other__"); }}
                  sx={{
                    borderRadius: `${Math.max(8, 8)}px`,
                    textTransform: "none", fontSize: 13, fontWeight: 600,
                    color: optionColor, border: `1.5px dashed ${optionColor}80`,
                    px: 2.2, py: 0.9, bgcolor: "transparent",
                    justifyContent: "flex-start",
                    "&:hover": { bgcolor: `${optionColor}08`, transform: "translateY(-1px)" },
                  }}
                >
                  + Other
                </Button>
              )}
              {hasOtherOption && dropdownVal === "__other__" && (
                <TextField
                  fullWidth size="small" placeholder="Type your answer and press Enter..."
                  value={otherText}
                  autoFocus
                  onChange={(e) => setOtherText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && otherText.trim()) {
                      advance(otherText.trim());
                      setOtherText("");
                      setDropdownVal("");
                    }
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}
                />
              )}
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, flexDirection: layoutDir, alignItems: "center" }}>
                {opts.map((opt, i) => (
                  <Button
                    key={i}
                    size="small"
                    onClick={() => advance(getLabel(opt))}
                    sx={{
                      borderRadius: `${Math.max(8, 8)}px`,
                      textTransform: "none",
                      fontSize: 13,
                      fontWeight: 600,
                      background: `linear-gradient(135deg, ${optionColor}, ${optionColor}dd)`,
                      color: "#fff",
                      border: "none",
                      px: 2.2, py: 0.9, minWidth: 80,
                      flex: layoutDir === "column" ? "1 1 100%" : "0 1 auto",
                      boxShadow: `0 3px 10px ${optionColor}30`,
                      display: "flex", alignItems: "center", gap: 0.7, justifyContent: "center",
                      "&:hover": { filter: "brightness(1.1)", transform: "translateY(-1px)", boxShadow: `0 6px 16px ${optionColor}50` },
                      "&:active": { transform: "translateY(0)" },
                    }}
                  >
                    {currentQ.imageChoices?.[i]?.image && (
                      <img src={currentQ.imageChoices[i].image} alt="" style={{ width: 18, height: 18, borderRadius: 4, objectFit: "cover" }} />
                    )}
                    {getLabel(opt)}
                  </Button>
                ))}
                {hasOtherOption && (
                  <Button
                    size="small"
                    onClick={() => { setOtherText(""); setDropdownVal("__other__"); }}
                    sx={{
                      borderRadius: `${Math.max(8, 8)}px`,
                      textTransform: "none",
                      fontSize: 13, fontWeight: 600,
                      color: optionColor,
                      border: `1.5px dashed ${optionColor}80`,
                      px: 2.2, py: 0.9, bgcolor: "transparent",
                      flex: layoutDir === "column" ? "1 1 100%" : "0 1 auto",
                      "&:hover": { bgcolor: `${optionColor}08`, transform: "translateY(-1px)" },
                    }}
                  >
                    + Other
                  </Button>
                )}
              </Box>
              {hasOtherOption && dropdownVal === "__other__" && (
                <TextField
                  fullWidth size="small" placeholder="Type your answer and press Enter..."
                  value={otherText}
                  autoFocus
                  onChange={(e) => setOtherText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && otherText.trim()) {
                      advance(otherText.trim());
                      setOtherText("");
                      setDropdownVal("");
                    }
                  }}
                  sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}
                />
              )}
            </Box>
          )}
          {currentQ?.skipOption && !currentQ?.required && (
            <Box sx={{ mt: 1.2, display: "flex", justifyContent: "flex-end" }}>
              <Button
                startIcon={<span style={{ fontSize: 14 }}>⏭</span>}
                onClick={() => advance("", { isSkip: true })}
                sx={{
                  fontSize: 11, fontWeight: 700, textTransform: "none",
                  color: headerColor, border: `1.5px dashed ${headerColor}`,
                  borderRadius: 2, px: 1.5, py: 0.6,
                  bgcolor: "rgba(0,0,0,0.02)",
                  transition: "all 0.2s ease",
                  "&:hover": { bgcolor: `${headerColor}10`, borderColor: headerColor, transform: "translateY(-1px)", boxShadow: `0 3px 10px ${headerColor}30` },
                }}
              >
                Skip this question
              </Button>
            </Box>
          )}
        </Box>
      );
    }

    // Carousel with buttons
    if (type === "carousel_with_buttons") {
      const cards = (currentQ?.options || []).map((o) => typeof o === "object" ? o : { title: String(o), description: "", image: "", buttonText: "Select" });
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8, mt: 1 }}>
          {cards.map((c, i) => (
            <Box key={i} sx={{ p: 1.2, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 2, display: "flex", gap: 1, alignItems: "center" }}>
              {c.image && <Box sx={{ width: 44, height: 44, borderRadius: 1.5, overflow: "hidden", flexShrink: 0, bgcolor: "#f9fafb" }}><img src={c.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /></Box>}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1f2937" }}>{c.title}</Typography>
                {c.description && <Typography sx={{ fontSize: 10, color: "#6b7280" }}>{c.description}</Typography>}
              </Box>
              <Button size="small" onClick={() => advance(c.title)} sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 10, fontWeight: 700, bgcolor: headerColor, color: "#fff", px: 1.5, "&:hover": { bgcolor: headerColor, filter: "brightness(0.9)" } }}>{c.buttonText || "Select"}</Button>
            </Box>
          ))}
          {currentQ?.skipOption && !currentQ?.required && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={() => advance("", { isSkip: true })} sx={{ fontSize: 10, fontWeight: 700, textTransform: "none", color: headerColor, border: `1px dashed ${headerColor}`, borderRadius: 1.5, px: 1, py: 0.4 }}>Skip</Button>
            </Box>
          )}
        </Box>
      );
    }

    // Order items
    if (type === "order_items") {
      const itemsList = (currentQ?.options || []).map((o) => typeof o === "object" ? o : { name: String(o), price: 0, qty: 1, image: "", buttonText: "Add" });
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6, mt: 1 }}>
          {itemsList.map((it, i) => (
            <Box key={i} sx={{ p: 1, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              {it.image && <Box sx={{ width: 36, height: 36, borderRadius: 1, overflow: "hidden", flexShrink: 0 }}><img src={it.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /></Box>}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{it.name}</Typography>
                {it.price ? <Typography sx={{ fontSize: 10, color: headerColor, fontWeight: 700 }}>₹{it.price}</Typography> : null}
              </Box>
              <Button size="small" onClick={() => advance(it.name)} sx={{ borderRadius: 1, textTransform: "none", fontSize: 10, fontWeight: 700, bgcolor: headerColor, color: "#fff", px: 1.2, py: 0.4 }}>{it.buttonText || "Add"}</Button>
            </Box>
          ))}
          {currentQ?.skipOption && !currentQ?.required && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={() => advance("", { isSkip: true })} sx={{ fontSize: 10, fontWeight: 700, textTransform: "none", color: headerColor, border: `1px dashed ${headerColor}`, borderRadius: 1.5, px: 1, py: 0.4 }}>Skip</Button>
            </Box>
          )}
        </Box>
      );
    }

    // Form — multi-field
    if (type === "form") {
      const fields = (currentQ?.settings?.fields || []).map((f) => ({ options: [], ...f }));
      const submitText = currentQ?.settings?.submitText || "Submit";
      return (
        <Box sx={{ mt: 1, p: 1.2, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          {fields.map((f, i) => {
            const key = f.name || `field_${i}`;
            return (
              <TextField key={i} fullWidth size="small" label={f.label || `Field ${i + 1}`} placeholder={f.placeholder || ""}
                type={f.type === "phone" ? "tel" : f.type === "email" ? "email" : f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                required={!!f.required}
                InputLabelProps={f.type === "date" ? { shrink: true } : undefined}
                onChange={(e) => setFormValues((p) => ({ ...p, [key]: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 11 } }}
              />
            );
          })}
          <Button fullWidth variant="contained" onClick={() => {
            const summary = fields.map((f, i) => `${f.label || f.name || `Field ${i+1}`}: ${formValues[f.name || `field_${i}`] || ""}`).join("\n");
            advance(summary);
            setFormValues({});
          }} sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 11, fontWeight: 700, bgcolor: headerColor, color: "#fff", py: 0.8, "&:hover": { bgcolor: headerColor, filter: "brightness(1.05)" } }}>{submitText}</Button>
          {currentQ?.skipOption && !currentQ?.required && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={() => advance("", { isSkip: true })} sx={{ fontSize: 10, fontWeight: 700, textTransform: "none", color: headerColor, border: `1px dashed ${headerColor}`, borderRadius: 1.5, px: 1, py: 0.4 }}>Skip</Button>
            </Box>
          )}
        </Box>
      );
    }

    // Ask contacts
    if (type === "ask_contacts") {
      const fs = currentQ?.settings?.fields || { name: { enabled: true, required: true }, email: { enabled: true, required: true }, phone: { enabled: false, required: false } };
      const fields = [
        { key: "name", label: fs.name?.label || "Full Name", placeholder: fs.name?.placeholder || "Enter name", required: !!fs.name?.required, type: "text", enabled: fs.name?.enabled !== false },
        { key: "email", label: fs.email?.label || "Email", placeholder: fs.email?.placeholder || "you@example.com", required: !!fs.email?.required, type: "email", enabled: fs.email?.enabled !== false },
        { key: "phone", label: fs.phone?.label || "Phone", placeholder: fs.phone?.placeholder || "+91 98765 43210", required: !!fs.phone?.required, type: "tel", enabled: !!fs.phone?.enabled },
      ].filter((f) => f.enabled);
      const submitText = currentQ?.settings?.submitText || "Submit";
      return (
        <Box sx={{ mt: 1, p: 1.2, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          {fields.map((f) => (
            <TextField key={f.key} fullWidth size="small" type={f.type} label={f.label + (f.required ? " *" : "")} placeholder={f.placeholder} required={f.required}
              onChange={(e) => setContactVals((p) => ({ ...p, [f.key]: e.target.value }))}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 11 } }} />
          ))}
          <Button fullWidth variant="contained" onClick={() => {
            const summary = fields.map((f) => `${f.label}: ${contactVals[f.key] || ""}`).join("\n");
            advance(summary);
            setContactVals({});
          }} sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 11, fontWeight: 700, bgcolor: headerColor, color: "#fff", py: 0.8 }}>{submitText}</Button>
          {currentQ?.skipOption && !currentQ?.required && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={() => advance("", { isSkip: true })} sx={{ fontSize: 10, fontWeight: 700, textTransform: "none", color: headerColor, border: `1px dashed ${headerColor}`, borderRadius: 1.5, px: 1, py: 0.4 }}>Skip</Button>
            </Box>
          )}
        </Box>
      );
    }

    // Appointment booking
    if (type === "appointment_booking") {
      const s = currentQ?.settings || {};
      const startHour = s.startHour || "09:00";
      const endHour = s.endHour || "17:00";
      const duration = Number(s.duration) || 30;
      const workingDays = s.workingDays || [1, 2, 3, 4, 5];
      const minDate = s.minDate || "";
      const maxDate = s.maxDate || "";
      const confirmMsg = s.confirmationMessage || "Your appointment is confirmed!";
      const slots = (() => {
        const out = [];
        let [sh, sm] = startHour.split(":").map(Number);
        let [eh, em] = endHour.split(":").map(Number);
        let cur = sh * 60 + sm;
        const end = eh * 60 + em;
        while (cur + duration <= end) { const h = Math.floor(cur / 60); const m = cur % 60; out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`); cur += duration; }
        return out;
      })();
      const isWorkingDay = (d) => d && workingDays.includes(new Date(d).getDay());
      return (
        <Box sx={{ mt: 1, p: 1.2, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          <TextField size="small" type="date" label="Pick a date" value={selDate} onChange={(e) => { setSelDate(e.target.value); setSelSlot(""); }} inputProps={{ min: minDate || undefined, max: maxDate || undefined }} InputLabelProps={{ shrink: true }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 11 } }} />
          {selDate && isWorkingDay(selDate) && (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(56px, 1fr))", gap: 0.4 }}>
              {slots.map((slot) => (
                <Box key={slot} onClick={() => setSelSlot(slot)} sx={{ py: 0.5, textAlign: "center", borderRadius: 1, fontSize: 10, fontWeight: 700, cursor: "pointer", border: "1.5px solid", borderColor: selSlot === slot ? headerColor : "#e5e7eb", bgcolor: selSlot === slot ? `${headerColor}10` : "#fafbff", color: selSlot === slot ? headerColor : "#374151" }}>{slot}</Box>
              ))}
            </Box>
          )}
          <Button fullWidth variant="contained" disabled={!selDate || !isWorkingDay(selDate) || !selSlot} onClick={() => {
            setMessages((p) => [...p, { sender: "bot", text: `✅ ${confirmMsg}\n\nDate: ${selDate}\nTime: ${selSlot}` }]);
            advance(`📅 ${selDate} at ${selSlot}`);
            setSelDate(""); setSelSlot("");
          }} sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 11, fontWeight: 700, bgcolor: headerColor, color: "#fff", py: 0.8, "&.Mui-disabled": { bgcolor: "#e5e7eb", color: "#9ca3af" } }}>Confirm Booking</Button>
          {currentQ?.skipOption && !currentQ?.required && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={() => advance("", { isSkip: true })} sx={{ fontSize: 10, fontWeight: 700, textTransform: "none", color: headerColor, border: `1px dashed ${headerColor}`, borderRadius: 1.5, px: 1, py: 0.4 }}>Skip</Button>
            </Box>
          )}
        </Box>
      );
    }

    // Authenticator / OTP
    if (type === "authenticator") {
      const s = currentQ?.settings || {};
      const length = Number(s.length) || 6;
      const codeType = s.codeType || "numeric";
      const method = s.method || "email";
      const resendCooldown = Number(s.resendCooldown) || 60;
      const charRegex = codeType === "numeric" ? /^[0-9]$/ : /^[a-zA-Z0-9]$/;
      const generateOtp = () => {
        if (codeType === "numeric") { let s = ""; for (let i = 0; i < length; i++) s += Math.floor(Math.random() * 10); return s; }
        const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; let s = ""; for (let i = 0; i < length; i++) s += chars[Math.floor(Math.random() * chars.length)]; return s;
      };
      const sendOtp = () => {
        const code = generateOtp();
        setOtpExpected(code);
        setOtpSent(true);
        setOtpDigits(Array(length).fill(""));
        setOtpResendIn(resendCooldown);
        setMessages((p) => [...p, { sender: "bot", text: `🔐 ${length}-digit code sent to ${method === "email" ? "email" : "phone"}.\n\n📋 Demo: ${code}` }]);
      };
      return (
        <Box sx={{ mt: 1, p: 1.2, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 2 }}>
          {!otpSent ? (
            <Button fullWidth variant="contained" onClick={sendOtp} sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 11, fontWeight: 700, bgcolor: headerColor, color: "#fff", py: 0.8 }}>Send Code</Button>
          ) : (
            <>
              <Box sx={{ display: "flex", gap: 0.5, mb: 1, justifyContent: "center" }}>
                {otpDigits.map((d, i) => (
                  <TextField key={i} id={`talktokam-preview-otp-${i}`} value={d}
                    onChange={(e) => {
                      const v = e.target.value.toUpperCase();
                      if (v && !charRegex.test(v)) return;
                      const u = [...otpDigits]; u[i] = v; setOtpDigits(u);
                      if (v && i < length - 1) { const n = document.getElementById(`talktokam-preview-otp-${i + 1}`); if (n) n.focus(); }
                      if (u.every((c) => c !== "")) {
                        const entered = u.join("");
                        if (entered.toUpperCase() === otpExpected.toUpperCase()) {
                          setMessages((p) => [...p, { sender: "bot", text: "✅ Verified!" }]);
                          advance(entered);
                          setOtpSent(false); setOtpDigits([]); setOtpExpected("");
                        } else {
                          setMessages((p) => [...p, { sender: "bot", text: currentQ?.errorMessage || "Invalid code.", isError: true }]);
                          setOtpDigits(Array(length).fill(""));
                        }
                      }
                    }}
                    sx={{ width: 28, "& .MuiOutlinedInput-root": { borderRadius: 1, fontSize: 14, fontWeight: 700, height: 36, "& input": { textAlign: "center", p: 0 } } }}
                  />
                ))}
              </Box>
              {otpResendIn > 0 ? (
                <Typography sx={{ fontSize: 9, color: "#9ca3af", textAlign: "center" }}>Resend in {otpResendIn}s</Typography>
              ) : (
                <Box component="span" onClick={sendOtp} sx={{ display: "block", textAlign: "center", fontSize: 10, color: headerColor, fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>Resend code</Box>
              )}
            </>
          )}
          {currentQ?.skipOption && !currentQ?.required && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button onClick={() => advance("", { isSkip: true })} sx={{ fontSize: 10, fontWeight: 700, textTransform: "none", color: headerColor, border: `1px dashed ${headerColor}`, borderRadius: 1.5, px: 1, py: 0.4 }}>Skip</Button>
            </Box>
          )}
        </Box>
      );
    }

    // Real-time search
    if (type === "Real_time_search") {
      const s = currentQ?.settings || {};
      const source = (s.source || "static").toLowerCase();
      const staticList = (currentQ?.options || []).map((o) => typeof o === "object" ? o.label || o.value || String(o) : String(o));
      const list = source === "api" ? rtsApiResults : staticList;
      const filtered = rtsQuery ? list.filter((o) => o.toLowerCase().includes(rtsQuery.toLowerCase())) : list;
      const placeholder = s.placeholder || "Type to search...";
      return (
        <Box sx={{ mt: 1, p: 1.2, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 2, display: "flex", flexDirection: "column", gap: 0.6 }}>
          <TextField size="small" fullWidth placeholder={placeholder} value={rtsQuery} onChange={(e) => { setRtsQuery(e.target.value); setRtsSelected(""); }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 11 } }} />
          {rtsQuery && filtered.length > 0 && (
            <Box sx={{ maxHeight: 120, overflowY: "auto", display: "flex", flexDirection: "column", gap: 0.3 }}>
              {filtered.slice(0, 8).map((opt, i) => {
                const label = typeof opt === "object" ? (opt?.label || opt?.value || opt?.name || JSON.stringify(opt)) : (opt || "");
                return (
                  <Box key={i} onClick={() => { setRtsSelected(label); setRtsQuery(label); }} sx={{ p: 0.7, borderRadius: 1, fontSize: 11, cursor: "pointer", bgcolor: rtsSelected === label ? `${headerColor}15` : "transparent", color: rtsSelected === label ? headerColor : "#374151", fontWeight: rtsSelected === label ? 700 : 500, "&:hover": { bgcolor: `${headerColor}10` } }}>{label}</Box>
                );
              })}
            </Box>
          )}
          {rtsSelected && (
            <Button fullWidth variant="contained" onClick={() => { advance(rtsSelected); setRtsQuery(""); setRtsSelected(""); }} sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 11, fontWeight: 700, bgcolor: headerColor, color: "#fff", py: 0.7 }}>Select</Button>
          )}
          {currentQ?.skipOption && !currentQ?.required && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={() => advance("", { isSkip: true })} sx={{ fontSize: 10, fontWeight: 700, textTransform: "none", color: headerColor, border: `1px dashed ${headerColor}`, borderRadius: 1.5, px: 1, py: 0.4 }}>Skip</Button>
            </Box>
          )}
        </Box>
      );
    }

    // Default — text input is in footer, not here
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 25px 60px rgba(0,0,0,0.15)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${headerColor}, ${headerColor}cc)`,
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            src={companyLogo}
            sx={{
              width: 36,
              height: 36,
              border: "2px solid rgba(255,255,255,0.3)",
            }}
          />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>
              {botName || "Chatbot"}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
              {description || "Online"}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => {
              setStep(0);
              setMessages([]);
              setInput("");
            }}
            sx={{
              color: "rgba(255,255,255,0.7)",
              "&:hover": { color: "#fff" },
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: "rgba(255,255,255,0.7)",
              "&:hover": { color: "#fff" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Chat */}
      <Box
        ref={chatRef}
        sx={{
          p: 2,
          minHeight: 280,
          maxHeight: "50vh",
          overflowY: "auto",
          bgcolor: "#fafbfc",
        }}
      >
        {/* Welcome */}
        {welcomeText && (
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            {avatar && (
              <img
                src={avatar}
                alt=""
                style={{ width: 24, height: 24, borderRadius: "50%" }}
              />
            )}
            <Box
              sx={{
                px: 1.5,
                py: 1,
                bgcolor: "#fff",
                borderRadius: "12px 12px 12px 4px",
                border: "1px solid #f0f0f0",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              }}
            >
              <Typography sx={{ fontSize: 13 }}>{welcomeText}</Typography>
            </Box>
          </Box>
        )}

        {/* Messages */}
        {items.slice(0, step + 1).map((item, idx) => (
          <React.Fragment key={idx}>
            {/* Bot question */}
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              {avatar && (
                <img
                  src={avatar}
                  alt=""
                  style={{ width: 24, height: 24, borderRadius: "50%" }}
                />
              )}
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  bgcolor: "#fff",
                  borderRadius: "12px 12px 12px 4px",
                  border: "1px solid #f0f0f0",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  maxWidth: "80%",
                }}
              >
                <Typography
                  sx={{ fontSize: 13 }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.text) }}
                />
              </Box>
            </Box>
            {item.media && (
              <Box sx={{ ml: 4, mb: 1 }}>
                <img
                  src={item.media}
                  alt=""
                  style={{ maxWidth: 180, borderRadius: 8 }}
                />
              </Box>
            )}
            {/* User answer */}
            {messages[idx] && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    background: messages[idx].isSkip
                      ? "transparent"
                      : `linear-gradient(135deg, ${optionColor}, ${optionColor}dd)`,
                    borderRadius: "12px 12px 4px 12px",
                    color: messages[idx].isSkip ? headerColor : "#fff",
                    border: messages[idx].isSkip
                      ? `1.5px dashed ${headerColor}`
                      : "none",
                    fontStyle: messages[idx].isSkip ? "italic" : "normal",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {messages[idx].isSkip && (
                    <span style={{ fontSize: 13 }}>⏭</span>
                  )}
                  {messages[idx].text?.startsWith('[IMG]') ? (
                    <img src={messages[idx].text.replace('[IMG]', '')} alt="upload" style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8, display: 'block' }} />
                  ) : messages[idx].text?.startsWith('data:image/') ? (
                    <img src={messages[idx].text} alt="upload" style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8, display: 'block' }} />
                  ) : (
                    <Typography sx={{ fontSize: 13, fontWeight: messages[idx].isSkip ? 600 : 400 }}>
                      {messages[idx].isSkip ? "Skipped" : messages[idx].text}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </React.Fragment>
        ))}

        {/* Input rendered below current question in chat area, aligned with
            where the question bubble starts (past the avatar), not the avatar edge */}
        {!done && !typing && (
          <Box sx={{ ml: avatar ? 4 : 0 }}>{renderInput()}</Box>
        )}

        {/* Typing */}
        {typing && (
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            {avatar && (
              <img
                src={avatar}
                alt=""
                style={{ width: 24, height: 24, borderRadius: "50%" }}
              />
            )}
            <Box
              sx={{
                px: 1.5,
                py: 1,
                bgcolor: "#fff",
                borderRadius: "12px 12px 12px 4px",
                display: "flex",
                gap: 0.5,
              }}
            >
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: headerColor,
                    animation: `pulse 1.2s infinite ${i * 0.2}s`,
                    "@keyframes pulse": {
                      "0%,100%": { opacity: 0.3 },
                      "50%": { opacity: 1 },
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Done */}
        {done && (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Typography sx={{ fontSize: 24, mb: 0.5 }}>🎉</Typography>
            <Typography
              sx={{ fontSize: 13, fontWeight: 700, color: "#166534" }}
            >
              Thank You!
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>
              All questions answered.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer — text input for text-type questions */}
      <Box sx={{ p: 1.5, borderTop: "1px solid #f0f0f0", bgcolor: "#fff" }}>
        {!done &&
          !typing &&
          currentQ &&
          ![
            "rating",
            "date_picker",
            "time_picker",
            "range",
            "multiple_options",
            "dynamic_question",
            "file_upload",
          ].includes(currentQ.type) &&
          !(currentQ.options && currentQ.options.length > 0) && (
            <Box>
              <Box sx={{
                display: "flex", gap: 1,
                animation: shake ? "talktokamShake 0.4s" : "none",
                "@keyframes talktokamShake": {
                  "0%,100%": { transform: "translateX(0)" },
                  "20%,60%": { transform: "translateX(-6px)" },
                  "40%,80%": { transform: "translateX(6px)" },
                },
              }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Type your answer..."
                  value={input}
                  onChange={(e) => { setInput(e.target.value); if (validationError) setValidationError(""); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && input.trim()) {
                      const result = validateAnswer(input.trim(), currentQ);
                      if (!result.valid) {
                        setValidationError(result.error);
                        setShake(true);
                        setTimeout(() => setShake(false), 400);
                        return;
                      }
                      advance(input.trim());
                      setInput("");
                      setValidationError("");
                    }
                  }}
                  error={!!validationError}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      fontSize: 12,
                      ...(validationError && { "& fieldset": { borderColor: "#dc2626", borderWidth: "1.5px" } }),
                    },
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={!input.trim()}
                  onClick={() => {
                    const result = validateAnswer(input.trim(), currentQ);
                    if (!result.valid) {
                      setValidationError(result.error);
                      setShake(true);
                      setTimeout(() => setShake(false), 400);
                      return;
                    }
                    advance(input.trim());
                    setInput("");
                    setValidationError("");
                  }}
                  sx={{ borderRadius: 2, minWidth: 36, bgcolor: headerColor }}
                >
                  <SendIcon fontSize="small" />
                </Button>
              </Box>
              {validationError && (
                <Typography sx={{ fontSize: 11, color: "#dc2626", mt: 0.5, pl: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <span>❌</span> {validationError}
                </Typography>
              )}
            </Box>
          )}
        {done && (
          <Typography
            sx={{ textAlign: "center", fontSize: 11, color: "#9ca3af", py: 1 }}
          >
            Flow complete ✓
          </Typography>
        )}
        <Typography
          sx={{ textAlign: "right", fontSize: 10, color: "#9ca3af", mt: 0.5 }}
        >
          Powered by{" "}
          <span style={{ color: "#6366f1", fontWeight: 600 }}>Talktokam</span>
        </Typography>
      </Box>
    </Dialog>
  );
};

export default BotPreviewDialogPopup;
